// file: DriveParticipant.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserInfoByUserId } from "../../../../../services/userServices/UserInfoService";
import {
  Participant,
  DriveItem,
} from "../../../../../interfaces/caseFileDrive/DriveFiles";
import { getDriveData } from "../../../../../services/fileServices/FileService";
import { useAuth } from "../../../../../firebase/AuthProvider";
import { mapYearsToDriveItems } from "../../../../../utilities/MapFunctions";
import { useDriveContext } from "../../../../../contexts/DriveContext";
import { Table } from "react-bootstrap";
import searchicon from "../../../../../assets/images/searchicon.svg";
import foldericon from "../../../../../assets/images/foldericon.svg";
import girdviewicon from "../../../../../assets/images/girdviewicon.svg";
import gridicon from "../../../../../assets/images/gridicon.svg";
import listviewicon from "../../../../../assets/images/listviewicon.svg";
import smallclockicon from "../../../../../assets/images/smallclockicon.svg";
import folderlgicon from "../../../../../assets/images/folderlgicon.svg";
import pdficon from "../../../../../assets/images/pdficon.svg";
import { formatFileSize } from "../../../../../utilities/fileUtils";
import { convertUtcToLocalDate } from "../../../../../utilities/dateUtils";
import backarrowbtn from "../../../../../assets/images/backarrowbtn.svg";
import "./CaseFileStyles.css";
import "./DriveParticipant.css";
import { FileText, FolderOpen, Grid3x3, List } from "lucide-react";
import Button from "../../../../ui/Buttons/Button";
import AvatarImage from "../../../../ui/Avatar/AvatarImage";

const DriveParticipant: React.FC = () => {
  const { currentUser, currentRoles } = useAuth();
  const { participantId } = useParams();
  const navigate = useNavigate();

  const [participant, setParticipant] = useState<Participant | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]); // for DriveList
  const { viewMode, setViewMode, searchTerm } = useDriveContext();
  const [loading, setLoading] = useState<boolean>(true);
  const hasHigherRoleThanSC = currentRoles?.some((role) => role.name !== "SC");
  const { employeeId } = useParams<{ employeeId?: string }>();
  const [itemStructure, setItemStucture] = useState<"grid" | "list">("list");
  const [employeeName, setEmployeeName] = useState<string>("");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        if (currentUser) {
          const uid = await currentUser.getIdToken();
          let data;
          if (employeeId && hasHigherRoleThanSC) {
            const userInfo = await getUserInfoByUserId(employeeId, uid);
            if (userInfo) {
              setEmployeeName(`${userInfo.firstName} ${userInfo.lastName}`);
            }
            data = await getDriveData(uid, employeeId);
          } else {
            data = await getDriveData(uid); // data is an array of Participant
          }
          const found = data.find(
            (p) => p.participantId.toString() === participantId
          );

          if (found) {
            setParticipant(found);
            // Convert the participant's years to DriveItem[] (folders)
            const yearItems = mapYearsToDriveItems(found.years);
            setItems(yearItems);
          } else {
            setParticipant(null);
            setItems([]);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [participantId, currentUser, employeeId, hasHigherRoleThanSC]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!participant) {
    return (
      <div>
        <button
          className="border rounded p-2 shadow-sm my-3 text-xs bg-white"
          onClick={() =>
            navigate(
              `/drive/caseFileDrive/${
                employeeId ? "employee/" + employeeId : ""
              }`
            )
          }
        >
          {"<"} Back
        </button>
        <p>There are no files for this participant.</p>
      </div>
    );
  }

  // Filter items by searchTerm (case-insensitive by name)
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle user clicking on a "year" folder
  const handleItemClick = (item: DriveItem) => {
    if (item.isFolder) {
      // We interpret the folder "name" or "id" as the year
      const yearClicked = item.id;
      navigate(`${yearClicked}`);
    } else {
      // It's a file, if that ever happens here
      console.log("Clicked a file in participant-level view:", item.name);
    }
  };

  return (
    <>
      <div className="common_content_inner mby_t_24">
        <div className="top top_bar_flx">
          <div className="top top_bar_flx participant">
            <div className="top_right_flx participant participant_files_top_right">
              <span
                className={`viewicon ${
                  itemStructure === "grid" ? "active-view" : ""
                }`}
                onClick={() => setItemStucture("grid")}
              >
                <Grid3x3 className="icon-svg" />
              </span>
              <span
                className={`viewicon ${
                  itemStructure === "list" ? "active-view" : ""
                }`}
                onClick={() => setItemStucture("list")}
              >
                <List className="icon-svg" />
              </span>
            </div>
          </div>
        </div>
        <div className="mb-2 flex gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-2">
            {employeeId && hasHigherRoleThanSC ? (
              <>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() => navigate("/drive/caseFileDrive")}
                >
                  My Team
                </div>
                <span> / </span>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() =>
                    navigate(`/drive/caseFileDrive/employee/${employeeId}`)
                  }
                >
                  {employeeName || employeeId}
                </div>
                <span> / </span>
                <span>{participant?.participantName}</span>
              </>
            ) : (
              <>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() => navigate("/drive/caseFileDrive")}
                >
                  Participant Files
                </div>
                <span> / </span>
                <span>{participant?.participantName}</span>
              </>
            )}
          </div>
          <div className="top_bar_tittle">
            <h2>
              {/* <button
                onClick={() =>
                  navigate(
                    `/drive/caseFileDrive/${
                      employeeId ? "employee/" + employeeId : ""
                    }`
                  )
                }
              >
                <img src={backarrowbtn} alt="back arrow" className="icon_sm" />
              </button> */}
              {/* Participant Files */}
              <div
                className="profile_link"
                onClick={() =>
                  navigate(`/participant/${participantId}/participant-snapshot`)
                }
              >
                View Profile
              </div>
            </h2>
          </div>
        </div>
        {itemStructure === "grid" && (
          <div className="participantfiles_grid">
            {filteredItems.map((item, index) => (
              <div
                className="participantfiles_card cursor-pointer"
                key={index}
                onClick={() => handleItemClick(item)}
              >
                <div className="participantfiles_grid_flx">
                  {/* <h3>
                          <span>
                            <img
                              src={item.isFolder ? foldericon : pdficon}
                              alt="foldericon"
                            />
                          </span>

                          {item.name}
                        </h3> */}
                  {item.isFolder ? (
                    <span className="tableicon-card">
                      {" "}
                      <FolderOpen className="tableicon-svg" />
                    </span>
                  ) : (
                    <span className="tableicon-card">
                      <FileText className="tableicon-svg" />
                    </span>
                  )}
                  <div className="grid-file-name">Sample File</div>
                  <div className="grid-updated">
                    Updated:{" "}
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </div>
                </div>
                <div className="participant-grid-bottom">
                  <div className="avatar-container">
                    <AvatarImage userId={item.name} />
                  </div>
                  <div className="participant-name">{item.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {itemStructure === "list" && (
          <Table responsive className="drive-table">
            <thead>
              <tr>
                <th className="th-head">Name </th>

                <th className="th-head">Participant </th>

                <th className="th-head">Created </th>

                <th className="th-head">Last Updated </th>

                <th className="th-head">File Size </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr key={index} onClick={() => handleItemClick(item)}>
                  {/* Folder icon column */}
                  <td>
                    <div className="tableicon-tab">
                      {item.isFolder ? (
                        <span className="tableicon">
                          {" "}
                          <FolderOpen className="tableicon-svg" />
                        </span>
                      ) : (
                        <span className="tableicon">
                          <FileText className="tableicon-svg" />
                        </span>
                      )}
                      <span className="font-normal text-[#354152] text-lg tracking-[0] leading-6 truncate">
                        Sample File
                      </span>
                    </div>
                  </td>

                  {/* Name */}
                  <td>{item.name}</td>

                  {/* Created date */}
                  <td>
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </td>

                  {/* Last Updated */}
                  <td>
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </td>

                  {/* File size */}
                  <td>{formatFileSize(item.size ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
};

export default DriveParticipant;
